import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

export interface WithdrawReviewUser {
  id: number;
  username: string;
}

export interface WithdrawReviewEnvironment {
  id: number;
  name: string;
  host: string;
  withdrawReviewFactorThreshold: number;
  withdrawReviewHardThreshold: number;
}

export interface WithdrawReviewRequest {
  id: number;
  environmentId: number;
  userId: number;
  status: string;
  channel: string;
  requestedAmount: number;
  approvedAmount: number | null;
  latestCashInAmount: number | null;
  currentBalanceAtRequest: number | null;
  factorAtRequest: number | null;
  declineReason: string | null;
  reviewedAt: string | null;
  usedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user: WithdrawReviewUser;
  environment: WithdrawReviewEnvironment;
  reviewedBy: WithdrawReviewUser | null;
}

export interface WithdrawReviewPageInfo {
  offset: number;
  limit: number;
  nextOffset: number | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  total: number;
}

export interface PaginatedWithdrawReviewRequests {
  requests: WithdrawReviewRequest[];
  pageInfo: WithdrawReviewPageInfo;
}

export interface WithdrawReviewFilters {
  environmentId?: number;
  status?: string;
  username?: string;
  limit?: number;
  offset?: number;
}

export interface ApproveWithdrawReviewInput {
  id: number;
  approvedAmount?: number;
}

export interface DeclineWithdrawReviewInput {
  id: number;
  declineReason?: string;
}

export interface ReopenWithdrawReviewInput {
  id: number;
}

const WITHDRAW_REVIEW_REQUESTS = gql`
  query WithdrawReviewRequests($filters: WithdrawReviewFiltersInput) {
    withdrawReviewRequests(filters: $filters) {
      requests {
        id
        environmentId
        userId
        status
        channel
        requestedAmount
        approvedAmount
        latestCashInAmount
        currentBalanceAtRequest
        factorAtRequest
        declineReason
        reviewedAt
        usedAt
        createdAt
        updatedAt
        user { id username }
        environment {
          id
          name
          host
          withdrawReviewFactorThreshold
          withdrawReviewHardThreshold
        }
        reviewedBy { id username }
      }
      pageInfo {
        offset
        limit
        nextOffset
        hasNextPage
        hasPreviousPage
        total
      }
    }
  }
`;

const APPROVE_WITHDRAW_REVIEW_REQUEST = gql`
  mutation ApproveWithdrawReviewRequest($input: ApproveWithdrawReviewInput!) {
    approveWithdrawReviewRequest(input: $input) {
      id
      status
      requestedAmount
      approvedAmount
      reviewedAt
      reviewedBy { id username }
    }
  }
`;

const DECLINE_WITHDRAW_REVIEW_REQUEST = gql`
  mutation DeclineWithdrawReviewRequest($input: DeclineWithdrawReviewInput!) {
    declineWithdrawReviewRequest(input: $input) {
      id
      status
      declineReason
      reviewedAt
    }
  }
`;

const REOPEN_WITHDRAW_REVIEW_REQUEST = gql`
  mutation ReopenWithdrawReviewRequest($input: ReopenWithdrawReviewInput!) {
    reopenWithdrawReviewRequest(input: $input) {
      id
      status
      declineReason
      approvedAmount
      reviewedAt
      reviewedBy { id username }
    }
  }
`;

class WithdrawReviewService {
  private static instance: WithdrawReviewService;

  private constructor() {}

  static getInstance(): WithdrawReviewService {
    if (!WithdrawReviewService.instance) {
      WithdrawReviewService.instance = new WithdrawReviewService();
    }
    return WithdrawReviewService.instance;
  }

  async getRequests(
    filters?: WithdrawReviewFilters,
  ): Promise<PaginatedWithdrawReviewRequests> {
    const cleanFilters: Record<string, any> = {};
    if (filters) {
      if (filters.environmentId) cleanFilters.environmentId = filters.environmentId;
      if (filters.status) cleanFilters.status = filters.status;
      if (filters.username) cleanFilters.username = filters.username;
      if (filters.limit != null) cleanFilters.limit = filters.limit;
      if (filters.offset != null) cleanFilters.offset = filters.offset;
    }
    const { data } = await apolloClient.query({
      query: WITHDRAW_REVIEW_REQUESTS,
      variables: Object.keys(cleanFilters).length > 0 ? { filters: cleanFilters } : {},
      fetchPolicy: 'network-only',
    });
    return data.withdrawReviewRequests;
  }

  async approve(input: ApproveWithdrawReviewInput): Promise<WithdrawReviewRequest> {
    const { data } = await apolloClient.mutate({
      mutation: APPROVE_WITHDRAW_REVIEW_REQUEST,
      variables: { input },
    });
    return data.approveWithdrawReviewRequest;
  }

  async decline(input: DeclineWithdrawReviewInput): Promise<WithdrawReviewRequest> {
    const { data } = await apolloClient.mutate({
      mutation: DECLINE_WITHDRAW_REVIEW_REQUEST,
      variables: { input },
    });
    return data.declineWithdrawReviewRequest;
  }

  async reopen(input: ReopenWithdrawReviewInput): Promise<WithdrawReviewRequest> {
    const { data } = await apolloClient.mutate({
      mutation: REOPEN_WITHDRAW_REVIEW_REQUEST,
      variables: { input },
    });
    return data.reopenWithdrawReviewRequest;
  }
}

export const withdrawReviewService = WithdrawReviewService.getInstance();
